import { Loader2, Inbox, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

export const PageHeader = ({ overline, title, subtitle, actions, testid }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8" data-testid={testid}>
    <div>
      {overline && <p className="overline text-[#EA580C] mb-2">{overline}</p>}
      <h1 className="font-ui text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-2 max-w-2xl leading-relaxed">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
  </div>
);

export const KpiCard = ({ label, value, hint, to, onClick, icon: Icon, tone = "default", testid }) => {
  const tones = {
    default: "text-slate-900",
    accent: "text-[#EA580C]",
    danger: "text-[#DC2626]",
    success: "text-[#15803D]",
    warning: "text-[#B45309]",
  };
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="overline text-slate-400">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-slate-300" strokeWidth={1.6} />}
      </div>
      <p className={`font-ui text-2xl md:text-[28px] font-bold tracking-tight mt-4 ${tones[tone]}`}>{value}</p>
      {hint && <p className="text-xs text-slate-400 mt-2">{hint}</p>}
    </>
  );
  if (to) return <Link to={to} className="kpi-card block" data-testid={testid}>{body}</Link>;
  return (
    <button type="button" onClick={onClick} className="kpi-card" data-testid={testid}>
      {body}
    </button>
  );
};

export const Loading = ({ label = "Loading…" }) => (
  <div className="flex items-center gap-3 text-sm text-slate-500 py-16 justify-center" data-testid="loading-state">
    <Loader2 className="h-4 w-4 animate-spin" /> {label}
  </div>
);

export const EmptyState = ({ title, subtitle, action, testid = "empty-state" }) => (
  <div className="text-center py-16 px-6" data-testid={testid}>
    <Inbox className="h-8 w-8 text-slate-300 mx-auto" strokeWidth={1.4} />
    <p className="font-ui font-semibold text-slate-800 mt-4">{title}</p>
    {subtitle && <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">{subtitle}</p>}
    {action && <div className="mt-6 flex justify-center">{action}</div>}
  </div>
);

export const ErrorState = ({ message }) => (
  <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="error-state">
    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{message}</span>
  </div>
);

export const StatusPill = ({ status }) => {
  const map = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    admitted: "bg-blue-50 text-blue-700 border-blue-200",
    scheduled: "bg-blue-50 text-blue-700 border-blue-200",
    waiting: "bg-amber-50 text-amber-700 border-amber-200",
    partial: "bg-amber-50 text-amber-700 border-amber-200",
    expiring: "bg-amber-50 text-amber-700 border-amber-200",
    suspended: "bg-red-50 text-red-700 border-red-200",
    void: "bg-red-50 text-red-700 border-red-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
    expired: "bg-red-50 text-red-700 border-red-200",
    failed: "bg-red-50 text-red-700 border-red-200",
  };
  const cls = map[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize ${cls}`}>
      {String(status || "—").replace(/_/g, " ")}
    </span>
  );
};

export const Footer = () => (
  <footer className="border-t border-slate-200 bg-white px-6 py-5 text-xs text-slate-400 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
    <span>© {new Date().getFullYear()} AJ Classic Healthcare. All rights reserved.</span>
    <span className="flex items-center gap-4">
      <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
      <Link to="/terms" className="hover:text-slate-700">Terms</Link>
      <span data-testid="digitide-credit">Developed by <span className="text-slate-600 font-medium">DigiTide</span></span>
    </span>
  </footer>
);
